import DataTable from 'datatables.net-bs5';
import RowGroup from 'datatables.net-rowgroup-bs5';

DataTable.use(RowGroup);

export default class DataTableModule {
  constructor (element) {
    this.element = element;
    this.dt = null;
    this._extSearchFn = null;
    this._attrFilters = new Map();
    this._tabHandler = null;

    this.init();
  }

  init () {
    // guard against double init
    this.destroy();

    const table = this.element.querySelector('table');

    this.dt = new DataTable(table, {
      order: [],
      responsive: true,
      deferRender: true,
      autoWidth: false,
      pageLength: 25,
      lengthMenu: [25, 50, 100],
      pagingType: 'simple_numbers',
      scrollX: true,
      language: {
        decimal: '',
        emptyTable: 'Keine Einträge verfügbar in Tabelle.',
        info: '_START_ bis _END_ von _TOTAL_ Einträgen',
        infoEmpty: 'Zeige 0 bis 0 von 0 Einträgen',
        infoFiltered: '(gefiltert von _MAX_ gesamten Einträgen)',
        infoPostFix: '',
        thousands: '.',
        lengthMenu: 'Zeige _MENU_ Einträge',
        loadingRecords: 'Lädt...',
        processing: '',
        search: 'Suche:',
        zeroRecords: 'Keine passenden Einträge gefunden.',
        paginate: { first: 'Erste', last: 'Letzte', next: 'Vor', previous: 'Zurück' },
        aria: { orderable: 'Nach dieser Spalte sortieren', orderableReverse: 'Diese Spalte umkehren' },
      },
    });

    // Recalculate when inside Bootstrap tab.
    this._tabHandler = (e) => {
      if (this.element.closest('.tab-pane')?.id === e.target.getAttribute('data-bs-target')?.replace('#', '')) {
        this.dt.columns.adjust();
      }
    };
    document.addEventListener('shown.bs.tab', this._tabHandler);

    // EXT SEARCH callback (handles attr-based filters)
    this._extSearchFn = (settings, data, dataIndex) => {
      if (settings.nTable !== this.dt.table().node()) return true;
      if (this._attrFilters.size === 0) return true;

      const rowNode = this.dt.row(dataIndex).node();

      for (const [attr, rule] of this._attrFilters.entries()) {
        if (!rule) continue;

        const values = this._readAttrValues(rowNode, attr); // array of lowercased strings

        // ARRAY-BASED (multiple selected checkboxes or selects)
        if (Array.isArray(rule.vals)) {
          if (rule.type === 'checkbox') {
            // Checkbox logic: no checkbox selected -> show NOTHING
            if (rule.vals.length === 0) return false;
          } else {
            // Select logic: empty -> no filter
            if (rule.vals.length === 0) continue;
          }

          // match if ANY selected value is present
          if (!rule.vals.some(sel => values.includes(sel))) return false;
          continue;
        }

        // SCALAR (text input substring search)
        if (rule.val) {
          if (rule.mode === 'equals') {
            if (!values.includes(rule.val)) return false;
          } else {
            if (!values.some(v => v.includes(rule.val))) return false;
          }
        }
      }

      return true;
    };
    DataTable.ext.search.push(this._extSearchFn);

    // Text inputs
    this.element.querySelectorAll('input[data-filter]').forEach((el) => {
      const cfg = this._parseFilterConfig(el.dataset.filter);
      if (!cfg) return;
      el.addEventListener('input', () => this._applyFilter(el, cfg));
    });

    // Selects
    this.element.querySelectorAll('select[data-filter]').forEach((el) => {
      const cfg = this._parseFilterConfig(el.dataset.filter);
      if (!cfg) return;
      const handler = () => this._applyFilter(el, cfg);
      el.addEventListener('change', handler);
      if (window.jQuery && typeof window.jQuery.fn.select2 === 'function') {
        window.jQuery(el).on('select2:select select2:unselect select2:clear', handler);
      }
    });

    // Checkboxes (grouped)
    this.element.querySelectorAll('input[type="checkbox"][data-filter]').forEach((el) => {
      const cfg = this._parseFilterConfig(el.dataset.filter);
      if (!cfg) return;
      const handler = () => this._applyCheckboxFilter(el, cfg);
      el.addEventListener('change', handler);
      el.addEventListener('input', handler);
    });
  }

  _applyFilter (el, cfg) {
    // column search (text input)
    if (typeof cfg.column === 'number' && el.tagName === 'INPUT') {
      const raw = (el.value || '').trim();
      this.dt.column(cfg.column).search(raw).draw();
    }

    // attr-based (select & text)
    if (typeof cfg.dataAttr === 'string') {
      if (el.tagName === 'SELECT') {
        const selected = Array.from(el.selectedOptions || [])
          .map(o => (o.hasAttribute('value') ? o.value : '').trim())
          .filter(Boolean)
          .map(v => v.toLowerCase());

        // empty select → NO filter
        if (selected.length) {
          this._attrFilters.set(cfg.dataAttr, { vals: selected });
        } else {
          this._attrFilters.delete(cfg.dataAttr);
        }
        this.dt.draw();
      } else {
        // text input
        const v = (el.value || '').trim().toLowerCase();
        if (v) {
          this._attrFilters.set(cfg.dataAttr, { val: v, mode: 'includes' });
        } else {
          this._attrFilters.delete(cfg.dataAttr);
        }
        this.dt.draw();
      }
    }
  }

  _applyCheckboxFilter (el, cfg) {
    if (typeof cfg.dataAttr !== 'string') return;

    const group = Array.from(
      this.element.querySelectorAll('input[type="checkbox"][data-filter]'),
    ).filter(cb => cb.dataset.filter === el.dataset.filter);

    const selected = group
      .filter(cb => cb.checked)
      .map(cb => (cb.hasAttribute('value') ? cb.value : '').trim())
      .filter(Boolean)
      .map(v => v.toLowerCase());

    // Checkbox rule ALWAYS stored — empty means "match NOTHING"
    this._attrFilters.set(cfg.dataAttr, { type: 'checkbox', vals: selected });

    this.dt.draw();
  }

  _readAttrValues (rowNode, attr) {
    const raw = (rowNode.getAttribute(`data-${attr}`) || '').trim();
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(x => String(x).toLowerCase());
    } catch {}
    return [raw.toLowerCase()];
  }

  _parseFilterConfig (str) {
    try {
      const cfg = JSON.parse(str);
      if (typeof cfg.column === 'number' || typeof cfg.dataAttr === 'string') return cfg;
      return null;
    } catch {
      return null;
    }
  }

  destroy () {
    if (this._tabHandler) {
      document.removeEventListener('shown.bs.tab', this._tabHandler);
      this._tabHandler = null;
    }
    if (this._extSearchFn) {
      const arr = DataTable.ext.search;
      const idx = arr.indexOf(this._extSearchFn);
      if (idx > -1) arr.splice(idx, 1);
      this._extSearchFn = null;
    }
    if (this.dt) {
      this.dt.destroy(true);
      this.dt = null;
    }
    this._attrFilters.clear();
  }
}